import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
  renderPublicHero,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import PublicWorkflowSteps from "../components/PublicWorkflowSteps";

const adlWorkflowSteps = [
  {
    title: "Manage Resident Care Plan",
    description: "Review and update the resident care plan.",
    icon: "pi pi-file-edit",
  },
  {
    title: "Auto-generate tasks",
    description: "Generate ADL tasks automatically.",
    icon: "pi pi-sparkles",
  },
  {
    title: "Start charting",
    description: "Record daily care with less manual entry.",
    icon: "pi pi-pencil",
  },
  {
    title: "Download report",
    description: "Export daily or monthly ADL reports.",
    icon: "pi pi-download",
  },
] as const;

const adlFeatureRows = [
  {
    feature: "Connect daily tasks to the care plan",
    whatItDoes: "Generate resident-specific activities of daily living and task details from the support documented in the care plan, then review before use.",
    whyItSavesTime: "Reduces manual task setup and gives staff one clear place to see what needs to be completed for each resident.",
  },
  {
    feature: "Chart personal care and mobility",
    whatItDoes: "Track routine activities such as bathing, dressing, grooming, toileting, eating, transfers, repositioning, mobility, and other daily support needs.",
    whyItSavesTime: "Replaces scattered paper lists with a repeatable workflow for the whole care team.",
  },
  {
    feature: "Record support levels and item details",
    whatItDoes: "Capture the level of support provided and item-specific values such as amount, size, frequency, completion, or number of times.",
    whyItSavesTime: "Preserves the detail needed for resident care without forcing every task into the same format.",
  },
  {
    feature: "Use clear daily status options",
    whatItDoes: "Record actions such as Done, Refused, or Not Recorded and keep the current status visible to authorized staff.",
    whyItSavesTime: "Makes incomplete work and resident refusals easier to identify and follow up on.",
  },
  {
    feature: "Add notes and staff initials",
    whatItDoes: "Include notes for context and retain staff initials with the daily activity record.",
    whyItSavesTime: "Keeps the who, what, and important exceptions together in the same record.",
  },
  {
    feature: "Generate daily and monthly reports",
    whatItDoes: "Create reports with recorded values, support-level indicators, staff initials, action colors, notes, and exceptions.",
    whyItSavesTime: "Turns routine charting into a record that is easier to review with the care team and during compliance work.",
  },
] as const;

const AdlPage = () => {
  const authExperience = useAuthExperience();
  const mobile = useResponsive().isMobile;

  return (
    <div style={publicPageContainerStyle}>
      <PublicWorkflowSteps
        title="Manage Resident Care Plan"
        description="Let the system generate resident-specific tasks from the care plan, then spend less time entering setup details and more time recording care."
        steps={adlWorkflowSteps}
        accentColor="#166534"
        borderColor="#cfe8d6"
        backgroundColor="#f3fbf5"
      />

      <div className="mt-3" style={publicSectionCardStyle}>
        <div style={{ color: "#166534", fontSize: mobile ? "1.4rem" : "1.75rem", fontWeight: 800, textAlign: "center" }}>
          ADL features for everyday care
        </div>
        <p style={{ ...publicMutedTextStyle, textAlign: "center", margin: "0.75rem auto 0", maxWidth: "820px" }}>
          Keep routine personal-care and mobility documentation consistent while giving staff room to record what actually happened.
        </p>

        {mobile ? (
          <div className="mt-4 flex flex-column gap-3">
            {adlFeatureRows.map((row) => (
              <div key={row.feature} style={{ border: "1px solid #cfe8d6", borderRadius: "18px", padding: "1rem", background: "#ffffff" }}>
                <div style={{ color: "#166534", fontWeight: 800, fontSize: "1rem" }}>{row.feature}</div>
                <div className="mt-2" style={publicMutedTextStyle}>{row.whatItDoes}</div>
                <div className="mt-2" style={{ color: "#166534", fontWeight: 700, lineHeight: 1.7 }}>{row.whyItSavesTime}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "860px" }}>
              <thead>
                <tr style={{ background: "#f3fbf5" }}>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #cfe8d6", color: "#166534" }}>Feature</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #cfe8d6", color: "#166534" }}>What Hub Provides</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #cfe8d6", color: "#166534" }}>Why It Saves Time</th>
                </tr>
              </thead>
              <tbody>
                {adlFeatureRows.map((row) => (
                  <tr key={row.feature}>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef7f0", color: "#0f172a", fontWeight: 700, verticalAlign: "top" }}>{row.feature}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef7f0", color: "#475569", verticalAlign: "top", lineHeight: 1.7 }}>{row.whatItDoes}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef7f0", color: "#166534", fontWeight: 700, verticalAlign: "top", lineHeight: 1.7 }}>{row.whyItSavesTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center" }}>
        <div style={{ color: "#166534", fontSize: mobile ? "1.35rem" : "1.7rem", fontWeight: 800 }}>
          Clear records for the whole care team
        </div>
        <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "780px" }}>
          {`${authExperience.brandName} keeps daily ADL activity, support details, initials, notes, and exceptions together so routine care is easier to communicate and review.`}
        </p>
      </div>
    </div>
  );
};

export default AdlPage;
